import type { RJSFSchema, UiSchema } from "@rjsf/utils";
import { pick } from "lodash";
import { useForm, useFormContext } from "react-hook-form";
import { CREATOR_AND_UP } from "common/authorization";
import type { Attachment } from "common/utils/customObjects";
import type {
  RoleName,
  SubmissionCategory,
  SubmissionModule,
} from "common-client/generated/graphql";
import type { GetSubmissionTypeForBuilderQuery } from "../../../../../generated/graphql";

export interface SubmissionsBuilderFormData {
  name?: string;
  description?: Maybe<string>;
  formStructure: {
    schema: RJSFSchema;
    uiSchema: UiSchema;
  };
  modules: Array<SubmissionModule>;
  attachments: Array<Attachment>;
  successMessage?: Maybe<string>;
  preamble?: Maybe<string>;
  postamble?: Maybe<string>;
  notificationEmails?: Maybe<string>;
  category?: SubmissionCategory;
  hiddenFromPublicByDefault: boolean;
  canCreateRolesAndGroupIds: Array<RoleName | string>;
  promoteSubmissions?: boolean;
}

export const useBuilderForm = useForm<SubmissionsBuilderFormData>;

export const useBuilderFormContext = useFormContext<SubmissionsBuilderFormData>;

export function getDefaultValues({
  existingSubmissionType,
  versionId,
}: {
  existingSubmissionType?: GetSubmissionTypeForBuilderQuery["submissionType"];
  versionId?: string;
} = {}): SubmissionsBuilderFormData {
  const staticDefaultValues = {
    formStructure: {
      schema: {},
      uiSchema: {},
    },
    modules: [],
    attachments: [],
    canCreateRolesAndGroupIds: CREATOR_AND_UP,
    hiddenFromPublicByDefault: true,
  };

  if (!existingSubmissionType || !versionId) {
    return staticDefaultValues;
  }

  const existingSubmissionTypeVersion = existingSubmissionType.versions.find(
    version => version.id === versionId,
  );

  if (!existingSubmissionTypeVersion) {
    return staticDefaultValues;
  }

  const existingSubmissionTypeFields = pick(existingSubmissionType, [
    "name",
    "description",
    "category",
    "modules",
    "hiddenFromPublicByDefault",
    "successMessage",
    "preamble",
    "postamble",
  ]);

  return {
    ...existingSubmissionTypeFields,
    formStructure: pick(existingSubmissionTypeVersion.formStructure, [
      "schema",
      "uiSchema",
    ]),
    attachments: existingSubmissionType.attachments.map(
      ({ type, customMapIds }) => ({
        type,
        ...(customMapIds ? { customMapIds } : {}),
      }),
    ) as Array<Attachment>,
    notificationEmails:
      existingSubmissionType.notificationEmails?.join("\n") ?? null,
    canCreateRolesAndGroupIds: [
      ...existingSubmissionType.canCreate,
      ...(existingSubmissionType.canCreateGroups.map(group => group.id) ?? []),
    ],
  };
}

export const DEFAULT_PREAMBLE = "Your record was successfully submitted!";

export const DEFAULT_POSTAMBLE = "Thank you for filling out this form.";
